import { Controller } from "@hotwired/stimulus";

// Shared promise so all instances on the same page fetch the list exactly once
let fontsCachePromise = null;

export default class extends Controller {
  static targets = ["input", "category"];
  static values = {
    url: { type: String, default: "/restaurant/settings/online_orders/iframe_menus/fonts" }
  };

  connect() {
    this.dropdown = null;
    this.activeIndex = -1;
    this.allFonts = [];

    // Ensure the input field has autocomplete off
    this.inputTarget.setAttribute("autocomplete", "off");

    // Bind event listeners for outside click closing
    this.closeDropdownBind = this.closeDropdown.bind(this);
  }

  disconnect() {
    this.removeDropdown();
  }

  // Focus event: Open dropdown and fetch fonts if not already loaded
  async onFocus() {
    if (this.allFonts.length === 0) {
      try {
        if (!fontsCachePromise) {
          fontsCachePromise = fetch(this.urlValue).then(res => {
            if (!res.ok) throw new Error("HTTP error " + res.status);
            return res.json();
          });
        }
        this.allFonts = await fontsCachePromise;
      } catch (error) {
        console.error("[FontPicker] Failed to load fonts:", error);
        return;
      }
    }
    this.showDropdown();
  }

  // Input event: Filter results as user types
  onInput() {
    if (!this.dropdown) {
      this.showDropdown();
    } else {
      this.renderResults();
    }
  }

  // Keydown event: Handle keyboard navigation
  onKeydown(event) {
    if (!this.dropdown) return;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        this.navigate(1);
        break;
      case "ArrowUp":
        event.preventDefault();
        this.navigate(-1);
        break;
      case "Enter":
        event.preventDefault();
        if (this.activeIndex >= 0) {
          const items = this.dropdown.querySelectorAll(".dropdown-item");
          if (items[this.activeIndex]) {
            items[this.activeIndex].dispatchEvent(new Event("mousedown"));
          }
        }
        break;
      case "Escape":
        event.preventDefault();
        this.removeDropdown();
        this.inputTarget.blur();
        break;
    }
  }

  showDropdown() {
    this.removeDropdown();

    // Create wrapper/dropdown structure matching Bootstrap 5 styles
    const wrapper = document.createElement("div");
    wrapper.className = "dropdown-menu show w-100 overflow-auto font-picker-menu";
    wrapper.style.maxHeight = "280px";
    wrapper.style.zIndex = "1050";
    wrapper.style.position = "absolute";
    wrapper.style.left = "0";
    wrapper.style.top = "100%";
    wrapper.style.marginTop = "6px";

    // Insert wrapper directly after the input's parent wrapper
    this.element.style.position = "relative";
    this.element.appendChild(wrapper);
    this.dropdown = wrapper;

    this.renderResults();
    document.addEventListener("mousedown", this.closeDropdownBind);
  }

  renderResults() {
    if (!this.dropdown) return;

    const query = this.inputTarget.value.trim().toLowerCase();
    
    // Filter fonts: show all (up to limit) if query is empty, else filter by name
    const matches = this.allFonts
      .filter(font => font.family.toLowerCase().includes(query))
      .slice(0, 15); // Limit display to top 15 results for performance

    if (matches.length === 0) {
      this.dropdown.innerHTML = `<div class="font-picker-menu__empty">No matching fonts found</div>`;
      this.activeIndex = -1;
      return;
    }

    // Dynamically load Google Fonts stylesheet for the matching subset
    this.loadFontStylesheets(matches.map(f => f.family));

    this.dropdown.innerHTML = "";
    this.activeIndex = -1;

    matches.forEach((font, index) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "dropdown-item";
      btn.style.fontFamily = `"${font.family}", sans-serif`;

      // Inner HTML structure
      btn.innerHTML = `
        <span class="font-picker-menu__name">${font.family}</span>
        <span class="font-picker-menu__category">${font.category}</span>
      `;

      // Use mousedown instead of click so selection occurs before input blur
      btn.addEventListener("mousedown", (e) => {
        e.preventDefault();
        this.selectFont(font);
      });

      // Hover sets keyboard active index
      btn.addEventListener("mouseenter", () => {
        this.setHighlight(index);
      });

      this.dropdown.appendChild(btn);
    });
  }

  selectFont(font) {
    // Set the category before dispatching the family's events: anything listening for those
    // (event-form-theme#update) reads every hidden input's current value, category included.
    if (this.hasCategoryTarget) this.categoryTarget.value = font.category || "";
    this.inputTarget.value = font.family;
    // Dispatch input and change events so preview frames/auto-saves know about the update
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }));
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }));
    this.removeDropdown();
  }

  navigate(direction) {
    const items = this.dropdown.querySelectorAll(".dropdown-item");
    if (items.length === 0) return;

    let newIndex = this.activeIndex + direction;
    if (newIndex < 0) newIndex = items.length - 1;
    if (newIndex >= items.length) newIndex = 0;

    this.setHighlight(newIndex);
    
    // Scroll active item into view
    const activeItem = items[newIndex];
    activeItem.scrollIntoView({ block: "nearest" });
  }

  setHighlight(index) {
    const items = this.dropdown.querySelectorAll(".dropdown-item");
    items.forEach(item => item.classList.remove("active"));
    
    this.activeIndex = index;
    if (index >= 0 && items[index]) {
      // Style active keyboard item
      items[index].classList.add("active");
    }
  }

  // Injects Google Fonts <link> tags dynamically into the document head
  loadFontStylesheets(families) {
    if (!families || families.length === 0) return;
    
    const formattedFamilies = families
      .map(family => `family=${family.replace(/\s+/g, "+")}`)
      .join("&");
    
    const url = `https://fonts.googleapis.com/css2?${formattedFamilies}&display=swap`;
    
    // Check if stylesheet is already loaded to prevent duplicate tags
    if (document.querySelector(`link[href^="${url}"]`)) return;

    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = url;
    document.head.appendChild(link);
  }

  closeDropdown(event) {
    if (this.element.contains(event.target)) return;
    this.removeDropdown();
  }

  removeDropdown() {
    if (this.dropdown) {
      this.dropdown.remove();
      this.dropdown = null;
    }
    this.activeIndex = -1;
    document.removeEventListener("mousedown", this.closeDropdownBind);
  }
}
